import { useMediaQuery, useTheme } from "@mui/material";
import {
  UiBadge,
  UiListItem,
  UiListItemIcon,
  UiListItemText,
  UiTooltip,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import { MouseEventHandler, useCallback, useMemo } from "react";

import { HelperAdapterNavLink } from "@/components/helpers/helper_adapter_navlink";
import { useContextApp } from "@/screens/app/context/app";

import { useStyles } from "./drawer_item.css";
import { IProps } from "./drawer_item.types";

export const DrawerItem = ({
  Icon,
  isExpanded,
  text,
  to,
  toAlt,
  hasBadge,
  onClick,
  ...props
}: IProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLessThanMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    actions: { drawerExpandToggle },
  } = useContextApp();

  const handleClick = useCallback<MouseEventHandler>(
    (event) => {
      if (isLessThanMedium) {
        drawerExpandToggle(false);
      }

      onClick && onClick(event);
    },
    [drawerExpandToggle, isLessThanMedium, onClick],
  );

  return useMemo(
    () => (
      <UiListItem
        activeClassName={classes["list_item--active"]}
        className={classes["list_item"]}
        button
        component={HelperAdapterNavLink}
        to={to}
        toAlt={toAlt}
        {...testAppendAttr("class", "drawer_item")}
        {...props}
        onClick={handleClick}
      >
        {isExpanded ? (
          <UiListItemIcon className={classes.list_item__icon}>
            {hasBadge ? (
              <UiBadge
                color="primary"
                variant="dot"
                overlap="rectangular"
                className={classes.list_item__badge}
              >
                <Icon />
              </UiBadge>
            ) : (
              <Icon />
            )}
          </UiListItemIcon>
        ) : (
          <UiTooltip
            classes={{
              tooltipPlacementRight: classes.list_item__icon__tooltip,
            }}
            placement="right"
            title={text}
          >
            <UiListItemIcon className={classes.list_item__icon}>
              {hasBadge ? (
                <UiBadge
                  color="primary"
                  variant="dot"
                  overlap="rectangular"
                  className={classes.list_item__badge}
                >
                  <Icon />
                </UiBadge>
              ) : (
                <Icon />
              )}
            </UiListItemIcon>
          </UiTooltip>
        )}
        {isExpanded && (
          <UiListItemText
            className={classes.list_item__text}
            primary={
              <UiTypography className={classes.list_item__text__typo}>
                {text}
              </UiTypography>
            }
          />
        )}
      </UiListItem>
    ),
    [classes, to, toAlt, handleClick, props, isExpanded, hasBadge, Icon, text],
  );
};
